import { createApiClient, type ApiClient } from '@leanin/api-client';

// Single source of truth for the typed API client. `baseUrl` is empty
// because the OpenAPI paths carry the `/api/v1` prefix themselves
// (see server/__workflow__/api-client-contract.test.ts). Same-origin
// requests are handled by Vite's dev proxy in development and by the
// production Node server (apps/web/server.ts) in deployed envs.
//
// Auth attachment. Two mutually-exclusive mechanisms, dev-bypass first:
//
//   1. Dev-bypass (local only): under the old Next setup, middleware promoted
//      the `dev_user_id` cookie to the `X-Dev-User-Id` header. There is no
//      middleware in Vite, so we promote it here. The cookie itself never
//      reaches the server (Vite's proxy doesn't forward it cross-origin in
//      some configs), but the header does — read by
//      `apps/server/src/_platform/auth/.../resolveDevBypassUserId`.
//
//   2. Clerk session token (every real env): we attach a FRESH Clerk session
//      JWT as `Authorization: Bearer`. The server's `authenticateRequest`
//      then verifies the header token networklessly. Relying on Clerk's
//      `__session` cookie alone is fragile: that JWT lives ~60s and Clerk
//      refreshes it via a redirect handshake an XHR/`fetch` can't follow, so
//      a stale cookie makes `authenticateRequest` return signed-out and the
//      API answers `UNAUTHENTICATED` for a genuinely signed-in user — the
//      failure seen right after OAuth sign-up, where the post-redirect
//      navigation chain races the cookie refresh. `getToken()` always hands
//      back a current token, eliminating that class.
//
// See CLAUDE.md "Browser-driven debugging".

const DEV_BYPASS_COOKIE = 'dev_user_id';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readDevBypassUserId(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${DEV_BYPASS_COOKIE}=([^;]+)`),
  );
  if (!m || m[1] === undefined) return null;
  const value = decodeURIComponent(m[1]);
  return UUID_RE.test(value) ? value : null;
}

// Registered by the ClerkProvider-scoped bridge in `main.tsx`. Returns a
// fresh Clerk session token, or null when there is no Clerk session (dev
// bypass, or signed-out). Held at module scope so the cached client picks up
// the current getter without being rebuilt.
let clerkTokenGetter: (() => Promise<string | null>) | null = null;
export function setApiTokenGetter(
  getter: (() => Promise<string | null>) | null,
): void {
  clerkTokenGetter = getter;
}

// Exported for unit tests — the `fetch` openapi-fetch is configured with.
export async function authFetch(
  input: Request | string | URL,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(
    init?.headers ?? (input instanceof Request ? input.headers : undefined),
  );
  const devUserId = readDevBypassUserId();
  if (devUserId !== null) {
    if (!headers.has('X-Dev-User-Id')) headers.set('X-Dev-User-Id', devUserId);
  } else if (clerkTokenGetter !== null && !headers.has('Authorization')) {
    const token = await clerkTokenGetter();
    if (token !== null) headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(input, { ...init, headers });
}

let cached: ApiClient | null = null;
export function getApiClient(): ApiClient {
  if (cached === null) {
    cached = createApiClient({ baseUrl: '', fetch: authFetch });
  }
  return cached;
}
